<template>
  <div class="mention-picker" data-testid="mention-picker">
    <p :id="headerId" class="mention-picker__header">{{ title }}</p>
    <ul
      v-if="rows.length"
      :id="listId"
      class="mention-picker__list"
      role="listbox"
      :aria-labelledby="headerId"
    >
      <li
        v-for="(employee, index) in rows"
        :id="optionId(employee)"
        :key="employee.employee_id"
        class="mention-picker__option"
        :class="{ 'mention-picker__option--active': index === activeIndex }"
        role="option"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        data-testid="mention-picker-option"
        @mousedown.prevent
        @mouseenter="activeIndex = index"
        @click="emit('select', employee)"
      >
        <ExAvatar
          size="small"
          :picture="employee.avatar || null"
          :first-name="nameParts(employee).first"
          :last-name="nameParts(employee).last"
        />
        <span class="mention-picker__name">{{ employee.display_name }}</span>
        <span v-if="employee.position_title" class="mention-picker__title">
          {{ employee.position_title }}
        </span>
      </li>
    </ul>
    <p
      v-else
      class="mention-picker__status"
      :class="{ 'mention-picker__status--error': error }"
      :role="error ? 'alert' : 'status'"
      data-testid="mention-picker-status"
    >
      {{ statusText }}
    </p>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  maxRows: {
    type: Number,
    default: 6,
  },
  title: {
    type: String,
    default: "People in this account",
  },
  listId: {
    type: String,
    default: "mention-picker-list",
  },
});

const emit = defineEmits(["select", "close", "active-change"]);

const activeIndex = ref(0);

const rows = computed(() => props.employees.slice(0, props.maxRows));

const headerId = computed(() => `${props.listId}-header`);

const statusText = computed(() => {
  if (props.error) {
    return "Couldn't load people. Try again.";
  }
  return props.loading ? "Searching..." : "No matching employees";
});

const optionId = (employee) => `${props.listId}-option-${employee.employee_id}`;

const nameParts = (employee) => {
  const [first = "", ...rest] = String(employee.display_name || "")
    .trim()
    .split(/\s+/);
  return { first, last: rest.length ? rest[rest.length - 1] : "" };
};

const activeOptionId = computed(() =>
  rows.value[activeIndex.value] ? optionId(rows.value[activeIndex.value]) : null
);

watch(
  () => rows.value.map((employee) => employee.employee_id).join("|"),
  () => {
    activeIndex.value = 0;
  }
);

watch(activeOptionId, (id) => emit("active-change", id), { immediate: true });

const moveActive = (step) => {
  activeIndex.value = (activeIndex.value + step + rows.value.length) % rows.value.length;
};

const selectActive = () => {
  const employee = rows.value[activeIndex.value];
  if (!employee) {
    return false;
  }
  emit("select", employee);
  return true;
};

/** Handles Up/Down/Enter/Esc from the owning input; returns true when the key was consumed. */
const handleKeydown = (event) => {
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    emit("close");
    return true;
  }
  if (!rows.value.length) {
    return false;
  }
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    moveActive(event.key === "ArrowDown" ? 1 : -1);
    return true;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    return selectActive();
  }
  return false;
};

defineExpose({ handleKeydown, selectActive });
</script>

<style scoped>
.mention-picker {
  width: 100%;
  padding: 6px 0;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
}

.mention-picker__header {
  margin: 0;
  padding: 6px 14px;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}

.mention-picker__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.mention-picker__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 1px 6px;
  padding: 10px 10px 10px 8px;
  border-radius: 6px;
  cursor: pointer;
}

.mention-picker__option--active {
  background: #f9fafb;
}

.mention-picker__name {
  flex-shrink: 0;
  max-width: 60%;
  overflow: hidden;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-picker__title {
  min-width: 0;
  overflow: hidden;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-picker__status {
  margin: 0;
  padding: 10px 14px;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}

.mention-picker__status--error {
  color: #b42318;
}
</style>
